import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../App";
import { StarDisplay } from "./StarRating";
import { LevelBadge } from "./LevelBadge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
import { 
  Heart, 
  HandHelping, 
  UserCheck, 
  Clock, 
  ShieldCheck, 
  Handshake,
  Image as ImageIcon
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const categoryIcons = {
  bienveillance: Heart,
  aide: HandHelping,
  comportement: UserCheck,
  ponctualite: Clock,
  honnetete: ShieldCheck,
  respect: Handshake,
};

const categoryLabels = {
  bienveillance: "Bienveillance",
  aide: "Aide",
  comportement: "Comportement",
  ponctualite: "Ponctualité",
  honnetete: "Honnêteté",
  respect: "Respect",
};

export const RatingCard = ({ rating, showRatedUser = true, showRater = false }) => {
  const { token } = useAuth();
  
  const CategoryIcon = categoryIcons[rating.category] || Heart;
  
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getPhotoUrl = (path) => {
    if (path && token) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
    }
    return null;
  };

  const formatDate = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: fr });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-4 bg-slate-50 rounded-2xl animate-fadeIn" data-testid="rating-card">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        {showRater && (
          <Link to={`/user/${rating.rater_id}`}>
            <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
              <AvatarImage src={getPhotoUrl(rating.rater_photo)} alt={rating.rater_name} />
              <AvatarFallback className="bg-[#FF3366] text-white text-sm font-bold">
                {getInitials(rating.rater_name)}
              </AvatarFallback>
            </Avatar>
          </Link>
        )}
        
        {showRatedUser && (
          <Link to={`/user/${rating.rated_user_id}`}>
            <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
              <AvatarFallback className="bg-[#FF3366] text-white text-sm font-bold">
                {getInitials(rating.rated_user_name)}
              </AvatarFallback>
            </Avatar>
          </Link>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              {showRater && (
                <Link 
                  to={`/user/${rating.rater_id}`}
                  className="font-bold text-slate-900 hover:text-[#FF3366] transition-colors text-sm"
                  data-testid="rater-link"
                >
                  {rating.rater_name}
                </Link>
              )}
              {showRatedUser && (
                <Link 
                  to={`/user/${rating.rated_user_id}`}
                  className="font-bold text-slate-900 hover:text-[#FF3366] transition-colors text-sm"
                  data-testid="rated-user-link"
                >
                  {rating.rated_user_name}
                </Link>
              )}
              {rating.rater_level && (
                <LevelBadge level={rating.rater_level} size="small" showLabel={false} />
              )}
            </div>
            <StarDisplay rating={rating.stars} size={14} showValue={false} />
          </div>

          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full text-xs font-medium text-slate-600" data-testid="rating-category">
              <CategoryIcon size={12} />
              {categoryLabels[rating.category] || rating.category}
            </span>
            <span className="text-xs text-slate-400">{formatDate(rating.created_at)}</span>
          </div>

          {/* Comment */}
          {rating.comment && (
            <p className="text-sm text-slate-600" data-testid="rating-comment">
              {rating.comment}
            </p>
          )}

          {/* Proofs */}
          {rating.proofs && rating.proofs.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {rating.proofs.map((proof) => (
                <Dialog key={proof.id}>
                  <DialogTrigger asChild>
                    <button 
                      className="relative group"
                      data-testid={`proof-${proof.id}`}
                    >
                      {proof.content_type?.startsWith("image/") ? (
                        <img
                          src={getPhotoUrl(proof.storage_path)}
                          alt="Preuve"
                          className="w-16 h-16 object-cover rounded-xl border-2 border-white shadow-sm"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-200 rounded-xl flex items-center justify-center">
                          <ImageIcon size={20} className="text-slate-400" />
                        </div>
                      )}
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl rounded-3xl">
                    {proof.content_type?.startsWith("image/") ? (
                      <img
                        src={getPhotoUrl(proof.storage_path)}
                        alt="Preuve"
                        className="w-full h-auto max-h-[80vh] object-contain rounded-2xl"
                      />
                    ) : proof.content_type?.startsWith("video/") ? (
                      <video
                        src={getPhotoUrl(proof.storage_path)}
                        controls
                        className="w-full max-h-[80vh] rounded-2xl"
                      />
                    ) : (
                      <div className="p-8 text-center">
                        <ImageIcon size={48} className="mx-auto mb-4 text-slate-400" />
                        <p>{proof.original_filename}</p>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
